<script setup lang="ts">
import type { PortableTextComponentProps } from '@portabletext/vue'
import type { PortableTextBlock, PortableTextSpan } from 'sanity';
import { computed } from 'vue';
import PortableEditor from '../../PortableEditor.vue';

interface MarkupTableCell {
  _type: string;
  _key: string;
  value?: PortableTextBlock[];
}

interface MarkupTableRow {
  _type: string;
  _key: string;
  cells?: MarkupTableCell[];
}

interface MarkupTable {
  _type: string;
  _key: string;
  /** Number of leading rows rendered as headers. Managed by the editor's table menu. */
  headerRows?: number;
  rows?: MarkupTableRow[];
}

const props = defineProps<PortableTextComponentProps<MarkupTable>>();

const rows = computed(() => props.value.rows ?? []);
const headerRowCount = computed(() => Math.max(0, props.value.headerRows ?? 0));
const headerRows = computed(() => rows.value.slice(0, headerRowCount.value));
const bodyRows = computed(() => rows.value.slice(headerRowCount.value));

/**
 * On narrow screens cells stack, so each one is prefixed with its column's
 * header text. Only the first header row is used as the label source.
 */
function columnLabel(columnIndex: number) {
  return cellToPlainText(headerRows.value[0]?.cells?.[columnIndex]?.value);
}

function cellToPlainText(blocks: MarkupTableCell['value']) {
  return blocks
    ?.map((block: PortableTextBlock) =>
      ((block.children ?? []) as PortableTextSpan[]).map((child) => child.text).join('')
    )
    .join('\n\n');
}
</script>

<template>
  <div class="block__table__wrapper">
    <table class="block__table">
      <thead v-if="headerRows.length">
        <tr v-for="row in headerRows" :key="row._key" class="block__table-row">
          <th v-for="cell in row.cells" :key="cell._key" class="block__table-header">
            <PortableEditor :content="cell.value" />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in bodyRows" :key="row._key" class="block__table-row">
          <td
            v-for="(cell, columnIndex) in row.cells"
            :key="cell._key"
            class="block__table-cell"
            :data-label="columnLabel(columnIndex)"
          >
            <PortableEditor :content="cell.value" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.block__table__wrapper {
  overflow-x: auto;
}

.block__table {
  width: 100%;
}

.block__table-cell {
  vertical-align: top;
  display: block;

  @screen tablet-wide {
    display: table-cell;
    min-width: 250px;
    width: 25%;
  }
}

.block__table-cell,
.block__table-header {
  padding: theme('spacing.16') theme('spacing.8');
  text-align: left;

  @screen tablet-wide {
    padding: theme('spacing.24') theme('spacing.16');
  }
}

.block__table-row {

  @screen tablet-wide {
    border-bottom: 1px solid var(--theme-text-color);
  }
}

.block__table-header {
  display: none;

  @screen tablet-wide {
    display: table-cell;
    min-width: 250px;
    width: 25%;
    vertical-align: top;
  }
}

.block__table-cell {
  border-bottom: 1px solid var(--theme-text-color);

  @screen tablet-wide {
    border: none;
  }

  &:before {
    content: attr(data-label);
    font-weight: bold;
    display: block;

    @screen tablet-wide {
      display: none;
    }
  }
}
</style>
